import React, { useEffect, useState } from "react";
import PatientService from "../../Services/PatientService";
import dayjs from "dayjs";
import { Modal, Button, Spin } from "antd";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Scatter,
  ErrorBar,
  ComposedChart,
  Legend,
} from "recharts";
import HRV from "../../assets/images/Svg/Hrv.svg";

const RespiratoryChart = ({ smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [chartData1D, setChartData1D] = useState([]);
  const [chartData7D, setChartData7D] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch1DData = async (date) => {
    if (!smartWatchId) return;
    const start = dayjs(date).format("DD/MM/YYYY");
    const payload = {
      type: "RESPIRATION",
      userId: smartWatchId,
      fromDate: start,
      toDate: start,
      offset: 0,
    };
    try {
      setLoading(true);
      const res = await PatientService.GarminRespirationDailyData(payload);
      console.log("RESPIRATION", res?.data);
      if (res?.data?.length > 0) {
        setChartData1D(res?.data);
      } else setChartData1D([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch 7D data (heartRate in the object outside RAW JSON)
  const fetch7DData = async (startDate, endDate) => {
    if (!smartWatchId) return;
    const start = dayjs(startDate).format("DD/MM/YYYY");
    const end = dayjs(endDate).format("DD/MM/YYYY");
    const payload = {
      type: "RESPIRATION",
      userId: smartWatchId,
      fromDate: start,
      toDate: end,
      offset: 0,
    };
    try {
      setLoading(true);
      const res = await PatientService.GarminRespirationWeeklyData(payload);
      if (res?.data?.length > 0) {
        console.log("RESPIRATION", res?.data);
        const data = res.data.map((item) => {
          const date = item.DATE || dayjs().format("DD/MM/YYYY");
          return {
            ...item,
            date,
          };
        });
        setChartData7D(data);
      } else setChartData7D([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DData(selectedDate);
    } else {
      const end = dayjs();
      const start = end.subtract(6, "day");
      fetch7DData(start, end);
    }
  }, [tab, selectedDate, smartWatchId]);

  const handlePrevDay = () => setSelectedDate(selectedDate.subtract(1, "day"));
  const handleNextDay = () => setSelectedDate(selectedDate.add(1, "day"));

  const handlePrev7D = () => {
    const start = dayjs(chartData7D[0]?.date, "DD/MM").subtract(7, "day");
    const end = dayjs(chartData7D[0]?.date, "DD/MM").subtract(1, "day");
    fetch7DData(start, end);
  };

  const handleNext7D = () => {
    const end = dayjs(chartData7D[chartData7D.length - 1]?.date, "DD/MM").add(
      7,
      "day"
    );
    const start = end.subtract(6, "day");
    fetch7DData(start, end);
  };

  return (
    <div>
      <div className="flex justify-center mb-4 gap-3">
        <Button
          type={tab === "1D" ? "primary" : "default"}
          onClick={() => setTab("1D")}
        >
          1D
        </Button>
        <Button
          type={tab === "7D" ? "primary" : "default"}
          onClick={() => setTab("7D")}
        >
          7D
        </Button>
      </div>

      {loading && <Loader />}

      {tab === "1D" && (
        <>
          <div className="flex items-center justify-center gap-4 mb-4">
            <Button onClick={handlePrevDay}>← Prev Day</Button>
            <span className="font-semibold">
              {selectedDate.format("DD/MM/YYYY")}
            </span>
            <Button onClick={handleNextDay}>Next Day →</Button>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData1D}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="TIME" />
                <YAxis />
                <Tooltip />
                <Legend />

                {/* <ErrorBar
                  dataKey="MIN"
                  width={4}
                  data={chartData1D.map((d) => ({
                    TIME: d.TIME,
                    value: d.MIN,
                    errorTop: d.MAX - d.MIN,
                    errorBottom: 0,
                  }))}
                  stroke="#9ca3af"
                /> */}

                <Scatter data={chartData1D} dataKey="MAX" fill="#3b82f6" />
                <Line
                  type="monotone"
                  dataKey="AVG"
                  stroke="#9ca3af"
                  strokeWidth={2}
                  dot={false}
                />

                <Scatter
                  data={chartData1D}
                  dataKey="MIN"
                  fill="#ffa3af"
                  stroke="#9ca3af"
                />

                {/* <Scatter
                  data={chartData1D}
                  dataKey="AVG"
                  fill="#3b82f6"
                  r={3}
                /> */}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {tab === "7D" && (
        <>
          <div className="flex justify-between mb-4">
            <Button onClick={handlePrev7D}>← Prev 7 Days</Button>
            <Button onClick={handleNext7D}>Next 7 Days →</Button>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData7D}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="SLEEPAVG"
                  stroke="#3b82f6"
                  strokeWidth={4}
                  dot
                />
                <Line
                  type="monotone"
                  dataKey="AWAKEAVG"
                  stroke="#ff8000"
                  strokeWidth={4}
                  dot
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* <div className="grid grid-cols-4 md:grid-cols-7 gap-3 mb-5">
            {chartData7D.map((item, idx) => {
              const dayName = item?.date
                ? dayjs(item.date, "DD/MM/YYYY").format("dddd")
                : "";
              const resting = item.restingHeartRateInBeatsPerMinute ?? 0;
              const max = item.maxHeartRateInBeatsPerMinute ?? 0;

              return (
                <div
                  key={idx}
                  className="bg-white shadow rounded-lg p-3 flex flex-col items-center gap-2"
                >
                  <p className="text-xs text-gray-500">{dayName}</p>

                  <div className="flex flex-col items-center justify-center gap-2 w-full">
                    <div className="flex items-center gap-2 w-full px-2">
                      <p className="text-xs text-gray-500">Resting Avg</p>
                      <p className="text-sm font-semibold text-red-500">
                        {resting} ms
                      </p>
                    </div>
                    <div className="flex items-center gap-2 w-full px-2">
                      <p className="text-xs text-gray-500">Max Avg</p>
                      <p className="text-sm font-semibold text-blue-500">
                        {max} ms
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div> */}
        </>
      )}
    </div>
  );
};

const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-50">
    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default RespiratoryChart;
